import {
  ORDER_DEFAULT_ITEM_COUNT,
  type EditorQuestion,
} from '../question/types'

/**
 * Shared client types and helpers for the Question Bank surface.
 *
 * The wire shapes mirror the backend contracts (`QuestionBankItemDto`,
 * `QuestionBankCategoryDto`, `QuestionBankListResponse`) which serialize as
 * camelCase JSON. `difficulty` is a bank-item-only attribute and never flows
 * into a quiz question (copy-on-insert drops it).
 */
export type QuestionType = 'MultipleChoice' | 'TrueFalse' | 'OrderSequence'
export type Difficulty = 'Easy' | 'Medium' | 'Hard'
export type Scope = 'Public' | 'Private'

export type BankItem = {
  id: string
  type: QuestionType
  text: string
  timeSeconds: number
  difficulty: Difficulty | null
  categoryId: string | null
  categoryName: string | null
  scope: Scope
  isOwnedByMe: boolean
  options: string[] | null
  correctOptionIndex: number | null
  correctAnswer: boolean | null
  items: string[] | null
  explanation: string | null
  updatedAt: string
}

export type BankListResponse = {
  items: BankItem[]
  total: number
  page: number
  pageSize: number
}

export type BankCategory = {
  id: string
  name: string
  scope: Scope
  itemCount: number
}

/** Scope filter values understood by `GET /items?scope=`. */
export type ScopeFilter = 'all' | 'public' | 'private'

/** Difficulty filter values; `all` means no difficulty constraint. */
export type DifficultyFilter = 'all' | Difficulty

export type BankFilters = {
  search: string
  categoryId: string | null
  scope: ScopeFilter
  difficulty: DifficultyFilter
  page: number
}

export const BANK_PAGE_SIZE = 12

export const DIFFICULTY_VALUES: Difficulty[] = ['Easy', 'Medium', 'Hard']

/**
 * Server-relevant slice of the filters — i.e. everything the list endpoint
 * actually filters on. All of these (including `difficulty`) are real
 * server-side filters, so they vary the fetch/query key and the page count.
 */
export type ServerFilters = {
  search: string
  categoryId: string | null
  scope: ScopeFilter
  difficulty: DifficultyFilter
  page: number
}

/**
 * Stable query keys so every surface (browser, picker) and every
 * invalidation (item/category mutations) key off identical tuples. The
 * root arrays let mutations invalidate by prefix (`['questionBank','items']`
 * matches every paged/filtered item query).
 */
export const questionBankKeys = {
  itemsRoot: ['questionBank', 'items'] as const,
  items: (filters: ServerFilters) =>
    ['questionBank', 'items', filters] as const,
  categories: () => ['questionBank', 'categories'] as const,
}

/**
 * Map a bank item into a fresh editor question (copy-on-insert). The
 * inverse of the backend's `MapItem`: only the fields relevant to the
 * item's type are carried over, with the same safe fallbacks the edit
 * route uses when hydrating from the API. `difficulty` is intentionally
 * dropped — it is not part of the quiz question schema.
 */
export function bankItemToEditorQuestion(item: BankItem): EditorQuestion {
  if (item.type === 'TrueFalse') {
    return {
      type: 'TrueFalse',
      text: item.text,
      timeSeconds: item.timeSeconds,
      correctAnswer: item.correctAnswer ?? true,
      explanation: item.explanation ?? undefined,
    }
  }
  if (item.type === 'OrderSequence') {
    return {
      type: 'OrderSequence',
      text: item.text,
      timeSeconds: item.timeSeconds,
      items:
        item.items ??
        Array.from({ length: ORDER_DEFAULT_ITEM_COUNT }, () => ''),
      explanation: item.explanation ?? undefined,
    }
  }
  return {
    type: 'MultipleChoice',
    text: item.text,
    timeSeconds: item.timeSeconds,
    options: item.options ?? ['', '', '', ''],
    correctOptionIndex: item.correctOptionIndex ?? 0,
    explanation: item.explanation ?? undefined,
  }
}
